import React, { useState } from 'react';
import styled from 'styled-components';
import { marqueeA, marqueeB, methods, patternCatKeys, patterns } from './renewalData';
import {
  Block,
  BlockTitleSm,
  Eyebrow,
  Inner,
  Lead,
  SectionHead,
  SectionNo,
  SectionShell,
  SectionTitle,
  buttonReset,
  token,
} from './renewalStyles';

const MarqueeWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const MarqueeLane = styled.div`
  overflow: hidden;
  border-top: ${({ $top }) => ($top ? '1px solid rgba(255,255,255,0.15)' : '0')};
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding: 18px 0;
`;

/* 배열을 두 번 이어붙인 뒤 -50% 로 이동시키면 이음매 없이 순환합니다. */
const MarqueeTrack = styled.div`
  display: flex;
  width: max-content;
  gap: 56px;
  animation: brit-marquee ${({ $duration }) => $duration} linear infinite;
  animation-direction: ${({ $reverse }) => ($reverse ? 'reverse' : 'normal')};

  span {
    font-size: clamp(18px, 2vw, 28px);
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #6e6e6e;
    white-space: nowrap;
  }
`;

const PatternNote = styled.p`
  margin: 0 0 32px;
  font-size: 15px;
  color: ${token.faint};
`;

const CatRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
`;

const CatButton = styled.button`
  ${buttonReset};
  padding: 12px 22px;
  min-height: 44px;
  font-size: 14px;
  font-weight: 700;
  border: 1px solid ${({ $on }) => ($on ? token.primary : 'rgba(255,255,255,0.28)')};
  background: ${({ $on }) => ($on ? token.primary : 'transparent')};
  color: ${({ $on }) => ($on ? '#fff' : token.darkText)};
  transition: background 0.2s, border-color 0.2s, color 0.2s;
`;

/* 다크 섹션 격자 — 컨테이너 border + 셀 outline, gap 0 (라이트 섹션과 동일 규칙) */
const PatternGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 0;
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;

  > * {
    outline: 1px solid rgba(255, 255, 255, 0.2);
  }
`;

const PatternPane = styled.div`
  background: ${({ $alt }) => ($alt ? '#0A0A0A' : '#000')};
  padding: 32px 28px;
`;

const PaneLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: ${token.primary};
  margin-bottom: ${({ $mb }) => $mb || '20px'};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ItemButton = styled.button`
  ${buttonReset};
  padding: 10px 16px;
  min-height: 44px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid ${({ $on }) => ($on ? '#fff' : 'rgba(255,255,255,0.22)')};
  background: ${({ $on }) => ($on ? '#fff' : 'transparent')};
  color: ${({ $on }) => ($on ? '#000' : token.darkText)};
  transition: background 0.2s, border-color 0.2s, color 0.2s;
`;

const DetailPane = styled(PatternPane)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DetailName = styled.div`
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 14px;
  line-height: 1.3;
`;

const DetailDesc = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.85;
  color: ${token.darkText};
`;

const DetailUsage = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding-top: 18px;

  .caption {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
    color: ${token.darkMuted};
    margin-bottom: 10px;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.8;
    color: ${token.darkText};
  }
`;

const StackTag = styled.span`
  padding: 7px 14px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-size: 12px;
  font-weight: 700;
  color: #fff;
`;

const MethodCard = styled.div`
  background: #000;
  padding: 36px 28px;
  transition: background 0.3s;

  &:hover {
    background: #141414;
  }

  .en {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.2em;
    color: ${token.primary};
    margin-bottom: 14px;
  }

  .ko {
    font-size: 20px;
    font-weight: 800;
    margin-bottom: 20px;
    line-height: 1.3;
  }

  .desc {
    margin: 0 0 20px;
    font-size: 14px;
    line-height: 1.75;
    color: ${token.darkText};
    white-space: pre-line;
  }

  .output {
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    padding-top: 16px;
  }

  .output .caption {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
    color: ${token.darkMuted};
    margin-bottom: 8px;
  }

  .output p {
    margin: 0;
    font-size: 13px;
    line-height: 1.7;
    color: ${token.darkText};
    white-space: pre-line;
  }
`;

export default function Strengths() {
  const [patCat, setPatCat] = useState('backend');
  const [patItem, setPatItem] = useState(0);

  const items = patterns[patCat].items;
  const detail = items[Math.min(patItem, items.length - 1)];

  // 카테고리를 바꾸면 세부 항목 선택은 항상 첫 번째로 되돌립니다.
  const onSelectCat = key => {
    setPatCat(key);
    setPatItem(0);
  };

  return (
    <SectionShell id="strengths" $bg="#000" $color="#fff">
      <Inner>
        <SectionHead $dark $gap="24px">
          <SectionNo>02</SectionNo>
          <SectionTitle>BRIT STRENGTHS</SectionTitle>
        </SectionHead>
        <Lead $color="#B5B5B5" $max="760px" $mb="56px">
          BRIT의 논리적 접근을 통해 문제 해결을 위한 최적의 방법을 찾아냅니다.
        </Lead>

        <Block $mb="96px">
          <MarqueeWrap>
            <MarqueeLane $top>
              <MarqueeTrack $duration="34s">
                {marqueeA.map((label, i) => (
                  <span key={`${label}-${i}`}>{label}</span>
                ))}
              </MarqueeTrack>
            </MarqueeLane>
            <MarqueeLane>
              <MarqueeTrack $duration="44s" $reverse>
                {marqueeB.map((label, i) => (
                  <span key={`${label}-${i}`}>{label}</span>
                ))}
              </MarqueeTrack>
            </MarqueeLane>
          </MarqueeWrap>
        </Block>

        <Block $mb="120px">
          <Eyebrow>DESIGN METHODOLOGY</Eyebrow>
          <BlockTitleSm $mb="12px">SDLC &amp; Architecture Patterns</BlockTitleSm>
          <PatternNote>세부 기술을 선택하면 적용 방식과 설명을 확인할 수 있습니다.</PatternNote>

          <CatRow role="tablist">
            {patternCatKeys.map(key => (
              <CatButton
                key={key}
                type="button"
                role="tab"
                aria-selected={patCat === key}
                $on={patCat === key}
                onClick={() => onSelectCat(key)}
              >
                {patterns[key].label}
              </CatButton>
            ))}
          </CatRow>

          <PatternGrid>
            <PatternPane>
              <PaneLabel>세부 기술</PaneLabel>
              <ChipRow>
                {items.map((item, i) => (
                  <ItemButton
                    key={item.name}
                    type="button"
                    aria-pressed={patItem === i}
                    $on={patItem === i}
                    onClick={() => setPatItem(i)}
                  >
                    {item.name}
                  </ItemButton>
                ))}
              </ChipRow>
            </PatternPane>

            <DetailPane $alt>
              <div>
                <PaneLabel $mb="12px">설명</PaneLabel>
                <DetailName>{detail.name}</DetailName>
                <DetailDesc>{detail.desc}</DetailDesc>
              </div>
              <DetailUsage>
                <div className="caption">BRIT 적용 사례</div>
                <p>{detail.usage}</p>
              </DetailUsage>
              <ChipRow>
                {detail.stack.map(s => (
                  <StackTag key={s}>{s}</StackTag>
                ))}
              </ChipRow>
            </DetailPane>
          </PatternGrid>
        </Block>

        <Block $mb="0">
          <Eyebrow>DEVELOPMENT METHODOLOGY</Eyebrow>
          <BlockTitleSm>문제해결을 위한 BRIT의 방법론</BlockTitleSm>
          <PatternGrid>
            {methods.map(method => (
              <MethodCard key={method.en} data-reveal="true">
                <div className="en">{method.en}</div>
                <div className="ko">{method.ko}</div>
                <p className="desc">{method.desc}</p>
                <div className="output">
                  <div className="caption">기본 산출물</div>
                  <p>{method.output}</p>
                </div>
              </MethodCard>
            ))}
          </PatternGrid>
        </Block>
      </Inner>
    </SectionShell>
  );
}
